import { createFileRoute, Link } from "@tanstack/react-router";
import { FileText, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/termos")({
  head: () => ({
    meta: [
      { title: "Termos e Condições — MEI Toolbox" },
      {
        name: "description",
        content:
          "Termos de uso e condições gerais do MEI Toolbox em 2026: regras de acesso, assinaturas via Paddle, uso do assistente de IA, responsabilidades e contato.",
      },
      { property: "og:title", content: "Termos e Condições — MEI Toolbox" },
      {
        property: "og:description",
        content:
          "Termos de uso e condições gerais do MEI Toolbox em 2026: regras de acesso, assinaturas via Paddle, uso do assistente de IA, responsabilidades e contato.",
      },

      { property: "og:url", content: "https://meitoolbox.com.br/termos" },
    ],
    links: [{ rel: "canonical", href: "https://meitoolbox.com.br/termos" }],
  }),
  component: TermosPage,
});

const ultimaAtualizacao = "6 de maio de 2026";

function TermosPage() {
  return (
    <>
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-hero opacity-95" />
        <div className="relative container mx-auto px-4 py-16 md:py-20 text-white">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
            <FileText className="h-3.5 w-3.5" />
            Termos de uso
          </div>
          <h1 className="mt-5 font-display text-4xl md:text-5xl font-extrabold leading-tight max-w-3xl text-balance">
            Termos e Condições
          </h1>
          <p className="mt-4 text-lg text-white/85 max-w-2xl">
            Ao usar o MEI Toolbox, você concorda com os termos abaixo. Leia com atenção.
          </p>
          <p className="mt-3 text-sm text-white/70">Última atualização: {ultimaAtualizacao}</p>
        </div>
      </section>

      <section className="container mx-auto px-4 py-12 pb-20 max-w-3xl">
        <article className="prose prose-neutral max-w-none space-y-8 text-muted-foreground leading-relaxed">
          <Block title="1. Quem somos">
            <p>
              Estes Termos e Condições regem o uso da plataforma{" "}
              <strong className="text-foreground">MEI Toolbox</strong>, operada por{" "}
              <strong className="text-foreground">MEI Toolbox</strong>, inscrita no CNPJ{" "}
              <strong className="text-foreground">63.046.089/0001-47</strong> ("nós", "nosso"). Ao
              acessar ou usar nossos serviços, você ("usuário") declara que leu, compreendeu e
              concorda com estes termos.
            </p>
          </Block>

          <Block title="2. Aceitação">
            <p>
              O uso continuado da plataforma constitui aceitação integral destes termos. Se você não
              concorda com qualquer parte, não utilize a plataforma. Você declara ter idade legal
              para abrir um MEI (18 anos ou mais) e, se aplicável, possuir autoridade para vincular
              sua organização.
            </p>
          </Block>

          <Block title="3. Descrição do serviço">
            <p>
              O MEI Toolbox oferece ferramentas online (gratuitas e pagas) voltadas a
              microempreendedores individuais, incluindo calculadoras, geradores de documentos,
              conteúdo informativo e assistente de IA. Recursos premium estão disponíveis mediante
              assinatura paga.
            </p>
          </Block>

          <Block title="4. Pagamentos e assinaturas">
            <p>
              Nosso processo de pedidos é conduzido por nosso revendedor online{" "}
              <strong className="text-foreground">Paddle.com</strong>. Paddle.com é o{" "}
              <strong className="text-foreground">Merchant of Record</strong> de todos os nossos
              pedidos. A Paddle atende a todas as solicitações de suporte ao cliente relacionadas a
              pagamentos e processa reembolsos.
            </p>
            <p>
              Os termos comerciais da compra (cobrança, renovação automática, impostos,
              cancelamento, reembolsos) são regidos pelos{" "}
              <a
                href="https://www.paddle.com/legal/checkout-buyer-terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                Termos do Comprador da Paddle
              </a>
              . Assinaturas renovam automaticamente até que sejam canceladas pelo usuário no painel
              de conta.
            </p>
          </Block>

          <Block title="5. Uso aceitável">
            <p>
              Você concorda em <strong className="text-foreground">não</strong>:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Usar a plataforma para fins ilegais, fraudulentos ou enganosos;</li>
              <li>Enviar spam, malware ou conteúdo malicioso;</li>
              <li>Violar direitos de propriedade intelectual de terceiros;</li>
              <li>Tentar acessar sistemas, contas ou dados sem autorização;</li>
              <li>Realizar engenharia reversa, scraping massivo ou contornar limites técnicos;</li>
              <li>Revender, sublicenciar ou redistribuir o serviço sem autorização.</li>
            </ul>
          </Block>

          <Block title="6. Conta e credenciais">
            <p>
              Você é responsável por manter a confidencialidade de suas credenciais e por toda
              atividade realizada em sua conta. Forneça informações precisas e mantenha-as
              atualizadas.
            </p>
          </Block>

          <Block title="7. Propriedade intelectual">
            <p>
              Todo o conteúdo, software, design, marca e materiais do MEI Toolbox são de nossa
              propriedade ou licenciados a nós. Concedemos a você uma licença limitada, não
              exclusiva e intransferível para uso pessoal/profissional dentro do plano contratado.
            </p>
          </Block>

          <Block title="8. Assistente de IA">
            <p>
              O assistente de IA do MEI Toolbox gera respostas automatizadas que podem conter
              imprecisões. As respostas{" "}
              <strong className="text-foreground">
                não substituem aconselhamento contábil, jurídico ou tributário profissional
              </strong>
              . Sempre confirme informações críticas com um contador ou no Portal do Empreendedor.
            </p>
            <p>
              É proibido usar o assistente para gerar conteúdo ilegal, ofensivo, discriminatório,
              fraudulento ou que viole direitos de terceiros. Você é responsável por seus prompts e
              pelo uso que faz das respostas. Reservamo-nos o direito de filtrar, restringir ou
              suspender o acesso em caso de uso indevido.
            </p>
          </Block>

          <Block title="9. Disponibilidade do serviço">
            <p>
              Nos esforçamos para manter o serviço disponível, mas{" "}
              <strong className="text-foreground">
                não garantimos operação ininterrupta ou livre de erros
              </strong>
              . Manutenções programadas, falhas de terceiros ou eventos fora do nosso controle podem
              causar indisponibilidade.
            </p>
          </Block>

          <Block title="10. Limitação de responsabilidade">
            <p>
              Na máxima extensão permitida por lei, isentamo-nos de garantias implícitas. Nossa
              responsabilidade agregada está limitada ao valor pago por você nos 12 meses anteriores
              ao evento. Não respondemos por danos indiretos, lucros cessantes, perda de dados ou
              prejuízos consequentes. Esta limitação não se aplica a casos de dolo, fraude ou onde a
              lei vedar tal limitação.
            </p>
          </Block>

          <Block title="11. Suspensão e encerramento">
            <p>
              Podemos suspender ou encerrar seu acesso em caso de: violação destes termos,
              inadimplência, risco de fraude/segurança, ou por exigência legal. Após o encerramento,
              você poderá exportar dados durante prazo razoável; após esse período, os dados poderão
              ser excluídos.
            </p>
          </Block>

          <Block title="12. Lei aplicável e foro">
            <p>
              Estes termos são regidos pelas leis da República Federativa do Brasil. Fica eleito o
              foro do domicílio do contratante para dirimir quaisquer controvérsias, salvo
              disposição legal em contrário.
            </p>
          </Block>

          <Block title="13. Alterações nos termos">
            <p>
              Podemos atualizar estes termos a qualquer momento. A data da última atualização sempre
              estará no topo da página. Mudanças significativas serão comunicadas no site.
            </p>
          </Block>

          <Block title="14. Contato">
            <p>
              Dúvidas sobre estes termos? Fale com a gente em{" "}
              <strong className="text-foreground">contato@meitoolbox.com.br</strong>. Para questões
              de pagamento, reembolso e faturamento, contate diretamente a Paddle em{" "}
              <a
                href="https://paddle.net"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                paddle.net
              </a>
              .
            </p>
          </Block>
        </article>

        <div className="mt-12 rounded-2xl border border-border bg-surface/60 p-6 text-center">
          <Link
            to="/ferramentas"
            className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-soft hover:opacity-90 transition"
          >
            Ver todas as ferramentas <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-2xl font-bold text-foreground mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
